/**
 * Utility functions để xử lý file names và extensions
 */

/**
 * Lấy tên file đã decode và loại bỏ timestamp
 * 
 * @param {string} filename - Đường dẫn hoặc tên file (có thể có URL encoding)
 * @returns {string} - Tên file đã được làm sạch
 * 
 * @example
 * getCleanFileName("file%20name.pdf_1234567890") → "file_name.pdf"
 * getCleanFileName("https://.../file.pdf_123") → "file.pdf"
 */
export const getCleanFileName = (filename) => {
    if (!filename) return 'document.pdf';
    
    try {
        // Decode URL encoding (%20 → space, %C3%AA → ê, etc.)
        const decodedFilename = decodeURIComponent(filename);
        
        // Lấy tên file từ đường dẫn đầy đủ
        // "https://domain.com/path/file.pdf" → "file.pdf"
        const fileNameOnly = decodedFilename.split('/').pop();
        
        // Loại bỏ timestamp ở cuối (format: _số)
        // "file.pdf_1760078115970" → "file.pdf"
        const withoutTimestamp = fileNameOnly.replace(/_\d+$/, '');
        
        // Thay thế ký tự đặc biệt không hợp lệ trong tên file Windows/Linux
        const cleanName = withoutTimestamp
            .replace(/[<>:"/\\|?*]/g, '_') // Ký tự không hợp lệ → _
            .replace(/\s+/g, '_')          // Nhiều khoảng trắng → _
            .replace(/_+/g, '_')           // Nhiều _ liên tiếp → 1 _
            .trim();
        
        return cleanName || 'document.pdf';
    } catch (err) {
        console.error('Error cleaning filename:', err);
        return 'document.pdf';
    }
};

/**
 * Xác định loại file từ extension
 * Xử lý cả trường hợp có timestamp ở cuối
 * 
 * @param {string} filename - Tên file hoặc đường dẫn
 * @returns {string|null} - 'pdf', 'excel', 'docx', hoặc null nếu không hỗ trợ
 * 
 * @example
 * getFileExtension("document.pdf_123") → "pdf"
 * getFileExtension("data.xlsx_456") → "excel"
 * getFileExtension("unknown.txt") → null
 */
export const getFileExtension = (filename) => {
    if (!filename) return null;
    
    try {
        // Decode URL encoding
        const decodedFilename = decodeURIComponent(filename);
        
        // Lấy tên file từ đường dẫn
        const fileNameOnly = decodedFilename.split('/').pop();
        
        // Loại bỏ timestamp ở cuối nếu có
        const withoutTimestamp = fileNameOnly.replace(/_\d+$/, '');
        
        // Tách extension
        const parts = withoutTimestamp.split('.');
        if (parts.length < 2) return null; // Không có extension
        
        const ext = parts[parts.length - 1].toLowerCase();
        
        // Kiểm tra loại file
        if (ext === 'pdf') return 'pdf';
        if (['xlsx', 'xls', 'csv'].includes(ext)) return 'excel';
        if (ext === 'docx') return 'docx';
        
        return null;
    } catch (err) {
        console.error('Error getting file extension:', err);
        return null;
    }
};

/**
 * Kiểm tra xem file có phải PDF không
 * @param {string} filename - Tên file
 * @returns {boolean}
 */
export const isPDF = (filename) => getFileExtension(filename) === 'pdf';

/**
 * Kiểm tra xem file có phải Excel không
 * @param {string} filename - Tên file
 * @returns {boolean}
 */
export const isExcel = (filename) => getFileExtension(filename) === 'excel';

/**
 * Kiểm tra xem file có phải DOCX không
 * @param {string} filename - Tên file
 * @returns {boolean}
 */
export const isDocx = (filename) => getFileExtension(filename) === 'docx';